import { breakpoint } from '@porsche-design-system/components-js/styles';
import configJSON from '../config.json';
import { Config } from '../entities/config';
import { Environment } from '../entities/environment';
import {
  LOADING_STATUS,
  LOGGED_IN_USER_NO_NAME_STATUS,
  LOGGED_OUT_USER_STATUS,
  NavigationMode,
  NavigationModeType,
  NavigationTheme,
  NavigationThemeType
} from './constants';
import { Content, TMenu } from '../entities/content';
import { Application, ApplicationType } from './constants/app';
import { navStateStore } from '../state/nav-state-store';
import featureToggles from '../services/feature-toggles';

let rootElement: HTMLElement | null = null;

export function getRootElement(): HTMLElement {
  if (rootElement === null) {
    throw new Error('No root element present');
  }
  return rootElement;
}

export function loadRootElement(element: HTMLElement): void {
  rootElement = element;
}
export function getHTMLElementFromRoot(selector: string): HTMLElement | null {
  return getRootElement().querySelector(selector);
}

export function getConfig(env: Environment): Config {
  const locale = navStateStore.state.locale;
  const config = <Config>configJSON[env];

  if ((locale === 'en-CN' || locale === 'zh-CN') && featureToggles.isChineseCdnContentRequestToggleActive()) {
    return {
      ...config,
      CONTENT_URL: config.CONTENT_CN_URL,
      ASSETS_URL: config.ASSETS_CN_URL,
      LOGIN_REDIRECT_URL: config.LOGIN_REDIRECT_CN_URL
    };
  }
  return config;
}

export function hasKey<O>(obj: O, key: PropertyKey): key is keyof O {
  return obj && typeof obj === 'object' && key in obj;
}

export function isString(test: unknown): test is string {
  return typeof test === 'string' && test.length > 0;
}

export function getString(test: unknown): string {
  return typeof test === 'string' ? test : '';
}

/* istanbul ignore next */
export function getHTMLElement(el: unknown): HTMLElement | null {
  return el instanceof HTMLElement ? el : null;
}

/* istanbul ignore next */
export function focusOnElement(el: unknown): void {
  getHTMLElement(el)?.focus();
}

/* istanbul ignore next */
export function getHTMLInputElement(el: unknown): HTMLInputElement | null {
  return el instanceof HTMLInputElement ? el : null;
}

export function IsKeyboardClick(e: MouseEvent): boolean {
  return e.x === 0 && e.y === 0;
}

export function IsLoading(loggedInState: string): boolean {
  return loggedInState === LOADING_STATUS;
}

export function IsLoggedOut(loggedInState: string): boolean {
  return loggedInState === LOGGED_OUT_USER_STATUS;
}

export function IsLoggedIn(loggedInState: string): boolean {
  return !IsLoading(loggedInState) && !IsLoggedOut(loggedInState);
}

export function getUsername(loggedInState: string, loggedInText: string): string {
  return loggedInState === LOGGED_IN_USER_NO_NAME_STATUS ? loggedInText : loggedInState;
}

export function getQueryString(params: { [name: string]: string | number | undefined }): string {
  return Object.entries(params)
    .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
    .map(([name, value]) => [name, isString(value) ? value : value.toString()])
    .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
    .join('&');
}

export function constructCacheKey(...args: string[]): string {
  return args.join('---');
}

export function isDesktop(currentBreakpoint: number): boolean {
  return currentBreakpoint >= breakpoint.s;
}

export function getImageSize(currentBreakpoint: number): number {
  switch (currentBreakpoint) {
    case breakpoint.xxl:
    case breakpoint.xl:
      return 1920;
    case breakpoint.l:
      return 1360;
    case breakpoint.m:
      return 1024;
    case breakpoint.s:
      return 760;
    case breakpoint.xs:
      return 390;
    default:
      return 320;
  }
}

export function sanitizeMode(mode: NavigationModeType): NavigationModeType {
  if (!Object.keys(NavigationMode).includes(mode)) {
    return NavigationMode.navbar;
  }
  return mode;
}

export function sanitizeTheme(theme: NavigationThemeType): NavigationThemeType {
  if (!Object.keys(NavigationTheme).includes(theme)) {
    return NavigationTheme.light;
  }
  return theme;
}

export function sanitizeApp(app: string): ApplicationType {
  if (!Object.keys(Application).includes(app)) {
    return Application.default;
  }
  return app as ApplicationType;
}

export function constructThemeClass(mode: NavigationModeType, theme: NavigationThemeType): string {
  const sanitizedMode = sanitizeMode(mode);
  if (sanitizedMode === NavigationMode.navbar) {
    return NavigationMode.navbar;
  }

  const sanitizedTheme = sanitizeTheme(theme);
  return `${mode}_${sanitizedTheme}`;
}

export function isPromise<T>(value: unknown): value is Promise<T> {
  return (
    Boolean(value) &&
    typeof (value as Promise<T>).then === 'function' &&
    (value as Promise<T>)[Symbol.toStringTag] === 'Promise'
  );
}

export function addQueryParamsToUrl(url: string, params: Record<string, string>): string {
  return `${url}${url.includes('?') ? '&' : '?'}${getQueryString(params)}`;
}

export function getValidContentPathSegments(rootContentItem: TMenu, segments: string[]): string[] {
  let currentContentItem: TMenu | undefined = rootContentItem;

  const validSegments: string[] = [];
  for (const segment of segments) {
    currentContentItem = currentContentItem.children.find((child) => child.id === segment);
    if (currentContentItem === undefined || currentContentItem === null) {
      break;
    }
    validSegments.push(segment);
  }

  return validSegments;
}

export function getRootMenuItem(content: Content, key: keyof Content): TMenu | null {
  if (!['vehiclePurchase', 'shop', 'experience', 'services'].includes(key) || content[key] === undefined) {
    return null;
  }

  return content[key] as TMenu;
}

export function buildRoutingKeyHistoryFromContentPath(segments: string[]): string[] {
  const routingKeyHistory = [segments[0]];
  for (let i = 1; i < segments.length; i++) {
    routingKeyHistory.push(`${routingKeyHistory[i - 1]}/${segments[i]}`);
  }
  return routingKeyHistory;
}

export function isModelName(text: string): boolean {
  return ['718', '911', 'Taycan', 'Macan', 'Panamera', 'Cayenne'].includes(text);
}

export function isModelFuelTypeGasoline(model: string): boolean {
  const lowerCase = model.toLowerCase();
  return ['718', '911', 'macan'].includes(lowerCase);
}

export const getRightTheme = (theme: NavigationThemeType, mode: NavigationModeType): string => {
  if (mode === NavigationMode.transparent || mode === NavigationMode.hero) {
    return theme;
  }
  return 'light';
};

export function camelToKebapCase(camelString: string): string {
  return camelString.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`);
}

export function shallowCopySpecifiedAttributesWithDefault<
  S extends { [s: string]: unknown } | ArrayLike<unknown>,
  C extends { [s: string]: unknown } | ArrayLike<unknown>
>(
  sourceObject: S,
  copyObject: C,
  accessor: (object: S, key: keyof C) => unknown = (object, key) =>
    (object as Record<string | number | symbol, unknown>)[key]
): C {
  return Object.entries(copyObject).reduce((values, [key, value]) => {
    const valueFromSource = accessor(sourceObject, key as keyof C);
    if (typeof valueFromSource === 'string') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values[key as keyof C] = valueFromSource as any;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values[key as keyof C] = value as any;
    }
    return values;
  }, {} as C);
}
